import type { CharacterSchemas } from '@ddt/api-client';
import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { getServices } from '../services';

export type ItemTemplate = CharacterSchemas['ItemTemplateResponse'];

/** Estágio de uma arma configurado no painel (ADR 0036): a partir de `fromLevel`, as imagens que valem no lugar das do modelo. */
export interface WeaponStagePics {
  fromLevel: number;
  iconPic?: string;
  lookPic?: string;
}
/** As imagens por nível das armas, por modelo. */
export type WeaponLevels = Record<number, WeaponStagePics[] | undefined>;

/** As pastas de arte que valem para a arma no nível: o último estágio que começa nele ou antes. Vazio: valem as do modelo. */
export function weaponPicsAt(levels: WeaponLevels, templateId: number, level: number): { iconPic?: string; lookPic?: string } {
  let found: WeaponStagePics | undefined;
  for (const stage of levels[templateId] ?? []) if (stage.fromLevel <= level) found = stage;
  return { ...(found?.iconPic ? { iconPic: found.iconPic } : {}), ...(found?.lookPic ? { lookPic: found.lookPic } : {}) };
}

/**
 * Categorias de item do legado (`Shop_Goods.CategoryID`), com os nomes do painel do personagem. Seguem as regras do servidor
 * (`EquipmentRules`): 1 a 7 são as peças de visual e a arma, 13 a 17 as demais posições de equipamento, 10 e 11 vão na bolsa de
 * consumíveis e 12 na bolsa de missão.
 */
export const CATEGORY_NAMES: Record<number, string> = {
  1: 'Chapéu',
  2: 'Óculos',
  3: 'Cabelo',
  4: 'Olhos',
  5: 'Roupa',
  6: 'Rosto',
  7: 'Arma',
  8: 'Pulseira',
  9: 'Anel',
  10: 'Consumível',
  11: 'Material',
  12: 'Missão',
  13: 'Terno',
  14: 'Colar',
  15: 'Asas',
  16: 'Bolha',
  17: 'Arma auxiliar',
};

export function categoryName(id: number): string {
  return CATEGORY_NAMES[id] ?? `Categoria ${id}`;
}

/**
 * Modelos de item já consultados (nome, categoria, bônus). O catálogo é público e quase imutável, então fica em memória
 * a sessão toda. Pedidos simultâneos pelo mesmo modelo viram uma chamada só.
 */
export const useCatalog = defineStore('catalog', () => {
  const templates = reactive<Record<number, ItemTemplate | null>>({});
  const inflight = new Map<number, Promise<void>>();
  const weaponLevels = reactive<WeaponLevels>({});
  let levelsLoaded: Promise<void> | null = null;

  /** Baixa uma vez os estágios por nível das armas. Sem resposta, as armas ficam com as imagens do modelo e a próxima chamada tenta de novo. */
  function loadWeaponLevels(): Promise<void> {
    levelsLoaded ??= (async () => {
      const { api } = getServices();
      const { data } = await api.character.GET('/v1/catalog/weapon-levels');
      for (const w of data ?? []) {
        weaponLevels[Number(w.templateId)] = w.stages.map((s) => ({
          fromLevel: Number(s.fromLevel),
          ...(s.iconPic ? { iconPic: s.iconPic } : {}),
          ...(s.lookPic ? { lookPic: s.lookPic } : {}),
        }));
      }
    })().catch(() => {
      levelsLoaded = null;
    });
    return levelsLoaded;
  }

  async function load(templateId: number): Promise<void> {
    if (templateId in templates) return;
    let pending = inflight.get(templateId);
    if (!pending) {
      pending = (async () => {
        const { api } = getServices();
        const { data } = await api.character.GET('/v1/catalog/items/{templateId}', { params: { path: { templateId } } });
        templates[templateId] = data ?? null;
      })().finally(() => inflight.delete(templateId));
      inflight.set(templateId, pending);
    }
    await pending;
  }

  async function loadMany(ids: Iterable<number>): Promise<void> {
    await Promise.all([loadWeaponLevels(), ...[...new Set(ids)].map(load)]);
  }

  function name(templateId: number): string {
    return templates[templateId]?.name ?? `Item ${templateId}`;
  }

  return { templates, weaponLevels, load, loadMany, loadWeaponLevels, name };
});
